import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { ImageService } from 'services/image-service';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { Exercise, ExerciseDbService } from 'services/exercise-db.service';
import { SettingsService } from 'services/settings.service';
import { calculateAsymTrigramMatching, generateTrigrams, sleep } from '@cf-shared/shared-util';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { duplicateEquipment } from 'services/equipment-helpers';
import { createEquipmentUIItemAsync, EquipmentUIItem, getBestEquipmentIdBasedOnTemplate, getSourceEquipments } from 'services/equipment-ui-items';
import {
  equipmentIdForUnit, forgetEquipmentOnStat, legacyEquipmentId, recordEquipmentForUnit, setLegacyEquipmentId,
} from 'services/exercise-stat-equipment';
import { describeEquipment as describeEquipment } from 'util/formatting';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { CsHeaderButtons } from 'components/cs-header/cs-header';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { ButtonType } from '@cf-shared/enums';
import { isEquipmentIdTemplate } from '@cf-shared/data-model/equipment';
import { FitnessState, ExerciseStat } from '@cf-shared/data-model/user-state';
import { IEquipmentEditPage } from 'pages/equipment/equipment-edit-interface';
import { EquipmentSearchPageParams } from './equipment-search-interface';
import { resolveAnyExerciseByIdAsync } from 'services/exercise-resolve';
import { SearchOperationTracker, TrackedSearchOperation } from 'util/search-operation-tracker';
//import { TutorialController } from 'providers/tutorial-controller';

interface SearchOperation extends TrackedSearchOperation {
  foundItems: EquipmentUIItem[];
}

enum Mode {
  Select = 'select',
  Browse = 'browse',
}

@Component({
    selector: 'page-equipment-search',
    templateUrl: 'equipment-search-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class EquipmentSearchPage extends CsPage<EquipmentSearchPageParams> implements AfterViewInit {
  protected LL = LL;

  protected showSpinner = true;
  protected filterShowOnlySuggested = true;
  protected filterShowTemplates = false;
  protected filterShowMine = true;
  protected prevSearchText = '';
  protected searchText = '';
  protected searchDebouncerTimeout: ReturnType<typeof setTimeout> = undefined;
  protected filteredEquipment: EquipmentUIItem[] = [];
  protected sourceEquipment: EquipmentUIItem[]; /** used for "copy from..." */
  protected numSearchMatchesHidden: number; // number items that match the search but are hidden due to filters
  protected numCustomEquipment: number; // all user items existing, regardless of filters
  protected fitnessState: FitnessState;
  protected exerciseId: string;
  protected selectedItemId: string;
  protected exercise: Exercise;
  protected exerciseTemplateId: string;
  protected initalSelectedItemId: string;

  // Category dropdown for filtering
  protected selectedCategoryId: string;
  protected availableCategories: { id: string; name: string }[] = [];

  protected mode: Mode = Mode.Browse;
  protected Mode = Mode;

  private docWatcher: DocWatcher;
  protected CsHeaderButtons = CsHeaderButtons;

  constructor(
    public navCtrl: NavController,
    protected userDb: UserDb,
    protected imageService: ImageService,
    protected debug: DebugService,
    private cd: ChangeDetectorRef,
    protected exerciseDb: ExerciseDbService,
    private settings: SettingsService,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    private telemetry: TelemetryService,
    
    private popupMenu: PopupMenuProvider,
  ) {
    super();
  }

  getSelectedItem(): EquipmentUIItem {
    return this.filteredEquipment.find(e => e.id === this.selectedItemId);
  }
  setSelectedItem(item: EquipmentUIItem) {
    this.selectedItemId = item && item.id || undefined;
  }

  isButtonVisible(button: CsHeaderButtons) {
    return (this.getVisibleButtons() & button) !== 0;
  }

  getVisibleButtons() {
    switch (this.mode) {
      // in browse mode, always show back button only
      case Mode.Browse:
        return CsHeaderButtons.Back;

      // in select mode
      case Mode.Select:
        // show back only if selection unchanged
        if (!this.selectionChanged()) {
          return CsHeaderButtons.Back;
        }
        // for templates, show only cancel
        let selected = this.getSelectedItem();
        if (selected && this.isEquipmentIdTemplate(selected)) {
          return CsHeaderButtons.Cancel;
        }
        // for a selected non-template equipment, show apply and cancel
        return CsHeaderButtons.Accept | CsHeaderButtons.Cancel;
      default:
        this.logging.trackAbnormalError(LL.equipment.errors.unknownMode({ mode: this.mode }));
        return CsHeaderButtons.Back;
    }
  }

  ButtonType = ButtonType;

  async handleBackButtonClicked(buttonType: ButtonType) {
    this.telemetry.trackEvent('equipment_search_back_button_click', { buttonType: buttonType });
    await this.navCtrl.popAsync();
  }

  handleAcceptButtonClicked(event: Event | null, buttonType: ButtonType) {
    this.telemetry.trackEvent('equipment_search_accept_button_click', { buttonType: buttonType });
    if (event) {
      lockClick(event, this.handleAcceptButton());
    } else {
      void this.handleAcceptButton();
    }
  }

  handleCancelButtonClicked(event: Event | null, buttonType: ButtonType) {
    this.telemetry.trackEvent('equipment_search_cancel_button_click', { buttonType: buttonType });
    if (event) {
      lockClick(event, this.navCtrl.popAsync());
    } else {
      void this.navCtrl.popAsync();
    }
  }

  handleButtonClicked(button: CsHeaderButtons) {
    switch (button) {
      case CsHeaderButtons.Back:
        void this.handleBackButtonClicked(ButtonType.Header)
        break;
      case CsHeaderButtons.Accept:
        this.handleAcceptButtonClicked(null, ButtonType.Header)
        break;
      case CsHeaderButtons.Cancel:
        void this.handleCancelButtonClicked(null, ButtonType.Header);
        break;
      default:
        this.logging.trackAbnormalError(LL.equipment.errors.unknownButton({ button }));
        break;
    }
  }

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  isSearchInFocus() {
    let inputElement = this.searchInput.nativeElement as HTMLInputElement;
    if (inputElement) {
      return inputElement === document.activeElement;
    }
    return false;
  }

  ngAfterViewInit(): void {
    // no-op for now - kept for lifecycle interface
  }

  async loadAsync() {
    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    this.fitnessState.allEquipment = this.fitnessState.allEquipment || {};
    this.fitnessState.exerciseStats = this.fitnessState.exerciseStats || {};

    // If we have an exercise ID, load equipment based on the exercise's current weight unit
    let overrideUnit: 'kg' | 'lb' | undefined;
    if (this.exerciseId && this.fitnessState.exerciseStats[this.exerciseId]) {
      const exerciseStat = this.fitnessState.exerciseStats[this.exerciseId];
      if (exerciseStat.weight_unit === 'lb') {
        overrideUnit = 'lb';
      } else if (exerciseStat.weight_unit) {
        overrideUnit = 'kg';
      }
    }

    this.sourceEquipment = await getSourceEquipments(this.fitnessState, this.exerciseDb, this.userDb, this.settings.locale, false, overrideUnit, this.docWatcher);
    this.numCustomEquipment = this.sourceEquipment.filter(e => !this.isEquipmentIdTemplate(e)).length;

    // Build available categories from templates
    this.availableCategories = this.sourceEquipment
      .filter(e => this.isEquipmentIdTemplate(e))
      .map(e => ({ id: e.id, name: e.equipment.name }));

    if (this.mode === Mode.Select && !this.selectedItemId) {
      const exerciseStat = this.fitnessState.exerciseStats[this.exerciseId];
      let equipmentId: string | undefined;

      // Get the equipment ID for the current unit
      if (overrideUnit) {
        equipmentId = equipmentIdForUnit(exerciseStat, overrideUnit);
      }

      // Fall back to legacy equipmentId if no unit-specific ID exists
      if (!equipmentId) {
        equipmentId = legacyEquipmentId(exerciseStat);
      }

      if (equipmentId) {
        this.setSelectedItem(this.sourceEquipment.find(e => e.id === equipmentId));
      }
    }

    // In Select mode, initialize category dropdown
    if (this.mode === Mode.Select) {
      // if there's a pre-selected equipment, use its category so it's visible
      const selectedEquipment = this.sourceEquipment.find(e => e.id === this.selectedItemId);
      if (selectedEquipment && selectedEquipment.equipment.basedOnTemplateId) {
        this.selectedCategoryId = selectedEquipment.equipment.basedOnTemplateId;
      } else if (this.exerciseTemplateId) {
        // otherwise default to the exercise's suggested category
        this.selectedCategoryId = this.exerciseTemplateId;
      }
    }

    // if we are getting back from edit page, don't re-remember the initial selection
    if (!this.initalSelectedItemId) {
      this.initalSelectedItemId = this.selectedItemId || 'none';
    }

    // when returning from edit page, select the edited item
    let selectedItemId = this.navParams.data.selectedItemId;
    if (selectedItemId && this.fitnessState.allEquipment[selectedItemId]) {
      this.setSelectedItem(this.sourceEquipment.find(e => e.id === selectedItemId));
    }

    // in select mode, if there is no selected item
    if (this.mode === Mode.Select && !this.selectedItemId) {
      let fitnessPlan = await this.userDb.loadPlanAsync(this.docWatcher);
      let basedOnEquipmentTemplateLastTimeUsedId = getBestEquipmentIdBasedOnTemplate(this.sourceEquipment, this.fitnessState, fitnessPlan, this.exerciseTemplateId, overrideUnit);
      if (basedOnEquipmentTemplateLastTimeUsedId) {
        this.selectedItemId = basedOnEquipmentTemplateLastTimeUsedId;
      }

      // if none found, select the template
      if (!this.selectedItemId) {
        this.selectedItemId = this.exerciseTemplateId;
      }
    }
    if (!this.filteredEquipment.length) {
      void this.searchAsync(this.searchText);
    }
  }

  async scrollSelectedIntoViewAsync() {
    await sleep(1); // wait for the UI to update

    let index = this.filteredEquipment.findIndex(e => e.id === this.selectedItemId);
    let item = document.getElementById(`item-${index}`);
    if (item) {
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    let exerciseId = this.navParams.data.exercise;
    if (exerciseId && this.exerciseDb.isValidExerciseId(exerciseId)) {
      this.exercise = await resolveAnyExerciseByIdAsync(exerciseId, this.exerciseDb, this.userDb, this.docWatcher);
      if (this.exercise) {
        this.exerciseId = exerciseId;
        this.exerciseTemplateId = this.exercise.equipmentTemplateId;
        this.mode = Mode.Select;
      }
      if (!this.exerciseTemplateId) {
        this.filterShowOnlySuggested = false;
      }
    }

    void this.loadAsync();
  }

  async ionViewDidEnter() {
    // Not using tutorial here for now, but keeping this code for future reference
    //
    // // only do the tutorial when really doing the first select (i.e. not from dashboard)
    // if (this.mode === Mode.Select && this.filteredEquipment.length === 1 && this.selectedItemId === this.filteredEquipment[0].id && this.filteredEquipment[0].isTemplate) {
    //   await this.tutorialCtrl.playTutorialAsync({
    //     id: 'equipment-search.select',
    //     steps: [
    //       {
    //         highlightSelectors: ['page-equipment-search .config-item#item-0 .item-name', 'page-equipment-search .config-item#item-0 .item-selection'],
    //         message: `We have pre-selected the most likely equipment template for this exercise.`,
    //       },
    //       {
    //         highlightSelectors: ['page-equipment-search .config-item#item-0 .item-name cs-button'],
    //         message: `Please create your own specific equipment based on this template.`,
    //       },
    //     ]
    //   })
    // }
    this.selectionAccepted = false;
  }

  async ionViewWillLeave() {
    if (this.searchText !== '' && !this.selectionAccepted) {
      this.telemetry.trackEvent('equipment_search_rejected', { searchText: this.searchText });
    }

    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }

    // Note: Callbacks are now handled via the returnCallback mechanism in NavController.
    // Parent pages should use returnCallback param instead of passing callback functions.
  }

  async ionViewDidLeave() {
    const nextPageId = this.navCtrl.getActive().id;

    // Clear mode in case user uses primary navigation to go away from the page
    // Turns off admin mode so it doesn't persist for all navigation
    if (!['EquipmentEditPage'].includes(nextPageId) && !nextPageId.startsWith('equipment-edit')) {
      this.filteredEquipment = [];
    }
  }

  handleShowOnlySuggested() {
    let selectedItem = this.getSelectedItem();
    this.telemetry.trackEvent('equipment_search_show_all', { searchText: this.searchText, exercise: this.exerciseId, equipmentType: selectedItem && selectedItem.equipment && selectedItem.equipment.type });
    this.filterShowOnlySuggested = !this.filterShowOnlySuggested;
    this.cd.markForCheck();
    void this.searchAsync(this.searchText);
  }

  handleShowTemplates() {
    this.filterShowTemplates = !this.filterShowTemplates;
    if (!this.filterShowTemplates && !this.filterShowMine) {
      this.filterShowMine = true;
    }
    this.cd.markForCheck();
    void this.searchAsync(this.searchText);
  }
  handleShowMine() {
    this.filterShowMine = !this.filterShowMine;
    if (!this.filterShowMine && !this.filterShowTemplates) {
      this.filterShowTemplates = true;
    }
    this.cd.markForCheck();
    void this.searchAsync(this.searchText);
  }

  /** Handle category dropdown change */
  handleCategoryChange(categoryId: string) {
    this.selectedCategoryId = categoryId;
    this.cd.markForCheck();
    void this.searchAsync(this.searchText);
  }

  handleSearchChanged() {
    // start showing spinner immediately when user starts typing, even though we start the search only after the debounce
    // this makes the UI look more responsive
    this.showSpinner = true;
    this.cd.markForCheck();
    this.cd.detach();

    // debounce the input typing to prevent too many requests to the databases
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    // if we are deleting the search text
    if (this.searchText === '' && this.prevSearchText.length > 1) {
      this.telemetry.trackEvent('equipment_search_rejected', { searchText: this.prevSearchText });
    }
    this.prevSearchText = this.searchText;
    let text = this.searchText || '';
    this.searchDebouncerTimeout = setTimeout(() => {
      void this.searchAsync(text);
    }, 300);
  }

  private searchOps = new SearchOperationTracker<SearchOperation>();
  async searchAsync(searchText: string) {
    // separate into two scopes to prevent accidentally using untrimmed searchText
    // This can easily happen due to autocomplete and can cause subtle bugs that are hard to find.
    return this.searchAsync_internal(searchText.trim());
  }

  async searchAsync_internal(trimmedText: string) {
    let op = this.searchOps.start(trimmedText, { foundItems: [] });
    if (op === undefined) {
      return;
    }

    this.showSpinner = true;
    this.cd.markForCheck();
    this.cd.detach();

    if (this.searchOps.shouldCancel(op)) {
      this.searchOps.end(op);
      return;
    }

    let searchTrigrams = generateTrigrams(trimmedText);
    let lowercaseSearch = trimmedText.toLocaleLowerCase();

    let foundItems = this.sourceEquipment.filter(e => calculateAsymTrigramMatching(searchTrigrams, e.trigrams) > 0.2 || e.equipment.name.toLocaleLowerCase().includes(lowercaseSearch));

    let preFilterItems = foundItems;

    if (this.mode === Mode.Select) {
      // if a category is selected, filter by it
      if (this.selectedCategoryId) {
        foundItems = foundItems.filter(e => e.equipment.basedOnTemplateId === this.selectedCategoryId);
      }
      // hide template items in Select mode - we only want user equipment
      foundItems = foundItems.filter(e => !this.isEquipmentIdTemplate(e));
    }
    if (this.mode === Mode.Browse) {
      if (!this.filterShowTemplates) {
        foundItems = foundItems.filter(e => !this.isEquipmentIdTemplate(e));
      }
      if (!this.filterShowMine) {
        foundItems = foundItems.filter(e => this.isEquipmentIdTemplate(e));
      }
    }
    op.foundItems = foundItems

    this.filteredEquipment = op.foundItems;
    this.numSearchMatchesHidden = preFilterItems.length - foundItems.length;
    this.showSpinner = false;
    this.cd.reattach();
    this.cd.markForCheck();
    this.searchOps.end(op);

    await this.scrollSelectedIntoViewAsync();
  }

  /** This abstracts all intricacies of this being called as popup and then adding a new item which has to be
  selected when returning from the popup. */
  async invokeEquipmentEditPage(mode: 'create' | 'edit') {
    await this.navCtrl.pushAsync(IEquipmentEditPage, { equipment: this.selectedItemId, mode });
  }

  protected selectionAccepted = false;

  handleItemClicked(selected: boolean, item: EquipmentUIItem) {
    if (selected) {
      this.setSelectedItem(item);
    }
  }

  generateEquipmentUsedByText(item: EquipmentUIItem) {
    if (item.usedBy.length > 0) {
      return item.usedBy.join(', ');
    } else {
      return 'none';
    }
  }

  isEquipmentIdTemplate(item: EquipmentUIItem): boolean {
    return item && isEquipmentIdTemplate(item.id);
  }

  generateEquipmentDescription(item: EquipmentUIItem): string {
    return describeEquipment(item.equipment, this.settings.locale);
  }

  /** Get the usage count of this equipment (number of exercises using it) */
  getUsageCount(item: EquipmentUIItem): number {
    return item.usedBy.length;
  }

  /** Handle row click to select an item */
  handleRowClick(item: EquipmentUIItem) {
    this.setSelectedItem(item);
    this.cd.markForCheck();
  }

  /** Handle more options (ellipsis) menu click */
  async handleMoreOptions(event: Event, item: EquipmentUIItem) {
    event.stopPropagation();

    const buttons: PopupMenuButton[] = [];

    if (this.isEquipmentIdTemplate(item)) {
      // for templates, show create option
      buttons.push({
        text: LL.equipment.buttons.create(),
        handler: () => this.handleCreateButton(event, item),
      });
    } else {
      // for custom equipment, show modify, duplicate, delete options
      buttons.push({
        text: LL.equipment.buttons.modify(),
        handler: () => this.handleModifyButton(event, item),
      });
      buttons.push({
        text: LL.equipment.buttons.duplicate(),
        handler: () => this.handleDuplicateButton(event, item),
      });
      buttons.push({
        text: LL.equipment.buttons.delete(),
        handler: () => this.handleDeleteButton(event, item),
      });
    }

    await this.popupMenu.createMenu({
      buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  selectionChanged(): boolean {
    if (this.mode !== Mode.Select) {
      return false;
    }

    const selectedItem = this.getSelectedItem();
    const selectedItemId = selectedItem ? selectedItem.id : undefined;

    if ((selectedItem && this.isEquipmentIdTemplate(selectedItem)) || (this.initalSelectedItemId !== selectedItemId)) {
      return true;
    }

    return false;
  }
  // duplicate the currently selected item and select new item
  async duplicateSelectedItem() {
    const newItem = await this.duplicateItem(this.getSelectedItem());
    if (newItem) {
      // duplicateSelectedItem() is the "footer button" path — the user explicitly selected a row
      // and asked to duplicate, so it's expected that the new item becomes the selection.
      this.selectedItemId = newItem.id;
    }
  }

  /** Duplicate the given equipment without changing the user's row selection. Returns the new UI item. */
  async duplicateItem(item: EquipmentUIItem): Promise<EquipmentUIItem | undefined> {
    if (!item) return undefined;
    const newEquipment = duplicateEquipment(this.fitnessState, item.equipment);
    const allCustomExercises = await this.userDb.getAllCustomExercisesAsync();
    const newItem = await createEquipmentUIItemAsync(this.fitnessState, this.exerciseDb, allCustomExercises, newEquipment, this.docWatcher);
    this.filteredEquipment.push(newItem);
    return newItem;
  }


  async handleAcceptButton() {
    if (this.selectionAccepted) return;
    this.selectionAccepted = true;

    this.telemetry.trackEvent('equipment_search_accepted', { searchText: this.searchText, equipmentType: this.getSelectedItem().equipment.type, exercise: this.exerciseId });

    // this shouldn't ever happen because the button should be disabled, but just in case
    let selectedItem = this.getSelectedItem();
    if (this.isEquipmentIdTemplate(selectedItem)) {
      this.logging.trackAbnormalError(LL.equipment.errors.attemptToApplyTemplate({ id: selectedItem.id }), LL.errors.userFacing.equipmentCannotBeUsed())
    }

    if (this.exerciseId) {
      // make sure we have an exercise to save into
      let exerciseStats = this.fitnessState.exerciseStats[this.exerciseId];
      if (!exerciseStats) {
        exerciseStats = {} as ExerciseStat;
        this.fitnessState.exerciseStats[this.exerciseId] = exerciseStats;
      }

      // Get the selected equipment to determine its unit
      const selectedEquipment = this.fitnessState.allEquipment[this.selectedItemId];
      if (selectedEquipment && selectedEquipment.weight_unit) {
        // Save to the appropriate unit slot
        const unitKey = selectedEquipment.weight_unit === 'lb' ? 'lb' : 'kg';
        recordEquipmentForUnit(exerciseStats, unitKey, this.selectedItemId);
        
        // Update the exercise's weight_unit to match the selected equipment
        exerciseStats.weight_unit = selectedEquipment.weight_unit;
      } else {
        // Fallback: Save based on current weight unit of the exercise
        const currentUnit = exerciseStats.weight_unit === 'lb' ? 'lb' : 'kg';
        recordEquipmentForUnit(exerciseStats, currentUnit, this.selectedItemId);
      }
      
      // Also set legacy equipmentId for backward compatibility
      setLegacyEquipmentId(exerciseStats, this.selectedItemId);

      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    }

    await this.navCtrl.popAsync();
  }

  async handleModifyButton(e: Event, item?: EquipmentUIItem) {
    e.stopPropagation();

    // when invoked from a row's "more" menu we operate on that row directly so the user's
    // visible radio selection isn't disturbed; the footer button path falls back to selection.
    if (item) {
      await this.navCtrl.pushAsync(IEquipmentEditPage, { equipment: item.id, mode: 'edit' });
      return;
    }
    await this.invokeEquipmentEditPage('edit');
  }

  async handleDuplicateButton(e: Event, item?: EquipmentUIItem) {
    e.stopPropagation();

    if (item) {
      // duplicate the targeted item without altering the user's row selection
      const newItem = await this.duplicateItem(item);
      if (newItem) {
        await this.navCtrl.pushAsync(IEquipmentEditPage, { equipment: newItem.id, mode: 'create' });
      }
      return;
    }
    await this.duplicateSelectedItem();
    await this.invokeEquipmentEditPage('create');
  }

  async handleDeleteButton(e: Event, item?: EquipmentUIItem) {
    e.stopPropagation();

    let selectedItem = item ?? this.getSelectedItem();
    if (!selectedItem) {
      return;
    }

    const equipmentName = selectedItem.equipment?.name || selectedItem.equipment?.id || selectedItem.id || 'Unknown';

    let opt = {
      title: LL.equipment.alerts.deleteEquipment.title(),
      subTitle: LL.equipment.alerts.deleteEquipment.subtitle({ name: equipmentName }),
      message: selectedItem.usedBy.length > 0 ? LL.equipment.alerts.deleteEquipment.usedBy({ exercises: this.generateEquipmentUsedByText(selectedItem) }) : undefined,
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.delete(), role: AlertButtonRole.Primary, result: true, },
      ]
    };
    if (!await this.alertCtrl.showAlertAsync(opt)) {
      return;
    }

    for (let exerciseId in this.fitnessState.exerciseStats) {
      // clear the legacy and unit-specific equipment IDs that match
      forgetEquipmentOnStat(this.fitnessState.exerciseStats[exerciseId], selectedItem.id);
    }

    delete this.fitnessState.allEquipment[selectedItem.id];

    this.sourceEquipment.splice(this.sourceEquipment.indexOf(selectedItem), 1);
    this.filteredEquipment.splice(this.filteredEquipment.indexOf(selectedItem), 1);
    if (this.selectedItemId === selectedItem.id) {
      this.selectedItemId = undefined;
    }

    void this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
  }

  async handleCreateButton(e: Event, item?: EquipmentUIItem) {
    e.stopPropagation();

    // when invoked from a row's "more" menu we use that row's id; otherwise fall back to selection
    const targetItem = item ?? this.getSelectedItem();
    const templateId = targetItem && this.isEquipmentIdTemplate(targetItem) ? targetItem.id : undefined;

    // navigate to edit page - no pre-creation, equipment created in-memory in edit page
    await this.navCtrl.pushAsync(IEquipmentEditPage, {
      mode: 'create',
      templateId,
      suggestedTemplateId: this.exerciseTemplateId,
      returnCallback: 'onEquipmentCreated'
    });
  }

  /** Callback invoked when returning from equipment creation */
  onEquipmentCreated(_context: unknown, equipmentId: string) {
    if (!equipmentId) return;

    // reload data to include newly created equipment
    void this.loadAsync().then(() => {
      // find the equipment in source list
      const equipment = this.sourceEquipment.find(e => e.id === equipmentId);
      if (!equipment) return;

      // update filter to match the new equipment's template
      if (equipment.equipment.basedOnTemplateId) {
        this.selectedCategoryId = equipment.equipment.basedOnTemplateId;
      }

      // select the new equipment
      this.setSelectedItem(equipment);

      // re-run search with updated filter
      void this.searchAsync(this.searchText);
    });
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  showFiltersButton(): boolean {
    return true;
  }
}
